import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleContentRenderer from './components/ArticleContentRenderer';
import RelatedArticles from './components/RelatedArticles';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { useEffect } from 'react';
import useSEO from '@/hooks/useSEO';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const shareUrl = `http://peachbeat.cn/article/${article?.slug || slug}`;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51黑料网`;
    }
  }, [article]);

  useSEO({
    title: article ? `${article.title} - 51黑料网` : '文章未找到 - 51黑料网',
    description: article?.excerpt || '该文章可能已被删除或链接无效',
    canonicalUrl: shareUrl,
    ogImage: article?.image,
    ogType: 'article',
    publishedTime: article?.date ? `${article.date}T08:00:00+08:00` : undefined,
    modifiedTime: article?.date ? `${article.date}T08:00:00+08:00` : undefined,
    tags: article?.tags,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar transparent={false} />
        <main className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <i className="ri-error-warning-line text-6xl text-foreground-600 mb-4"></i>
          <h1 className="text-2xl font-heading font-bold text-foreground-100 mb-2">文章未找到</h1>
          <p className="text-foreground-400 mb-6">该文章可能已被删除或链接无效</p>
          <Link
            to="/"
            className="px-6 py-3 bg-primary-500 text-foreground-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, 3);

  const shareText = encodeURIComponent(article.title);

  const handleShare = (platform: string) => {
    const urls: Record<string, string> = {
      weibo: `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${shareText}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`,
    };
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    }
  };

  const wordCount = article.sections?.reduce(
    (acc: number, s: { type: string; content: string }) =>
      acc + (s.content?.length || 0),
    0
  ) || 0;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: {
      '@type': 'ImageObject',
      url: article.image,
      width: 1600,
      height: 900,
    },
    datePublished: `${article.date}T08:00:00+08:00`,
    dateModified: `${article.date}T08:00:00+08:00`,
    author: {
      '@type': 'Organization',
      name: article.author,
      url: 'http://peachbeat.cn/',
    },
    publisher: {
      '@type': 'Organization',
      name: '51黑料网',
      url: 'http://peachbeat.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': shareUrl,
    },
    articleSection: article.category,
    keywords: article.tags?.join(',') || '',
    wordCount,
    inLanguage: 'zh-CN',
    url: shareUrl,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar transparent={false} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main>
        {/* Hero Header */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={article.title}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-black/40"></div>
        </section>

        {/* Article Body */}
        <article className="max-w-4xl mx-auto px-4 md:px-6 -mt-32 relative z-10">
          {/* Meta Header */}
          <div className="bg-background-50/95 backdrop-blur-md rounded-t-lg border border-background-200/60 border-b-0 p-6 md:p-8">
            {/* Category Badge */}
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/15 text-primary-400 text-xs font-medium mb-4 hover:bg-primary-500/25 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-price-tag-3-line"></i>
              {article.category}
            </Link>

            {/* Title */}
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 leading-tight">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-5 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {(article.views / 1000).toFixed(1)}k 阅读
              </span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 border border-background-200/60 text-xs text-foreground-400"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Content Body */}
          <div className="bg-background-50/95 backdrop-blur-md rounded-b-lg border border-background-200/60 border-t-0 p-6 md:p-8">
            <ArticleContentRenderer sections={article.sections} />

            {/* Share Bar */}
            <div className="mt-10 pt-6 border-t border-background-200/40 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <span className="text-sm text-foreground-400">分享这篇文章：</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleShare('weibo')}
                  className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 border border-background-200/60 text-foreground-400 hover:text-primary-400 hover:border-primary-400/40 transition-colors duration-200 cursor-pointer"
                  aria-label="分享到微博"
                >
                  <i className="ri-weibo-line text-base"></i>
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 border border-background-200/60 text-foreground-400 hover:text-accent-400 hover:border-accent-400/40 transition-colors duration-200 cursor-pointer"
                  aria-label="分享到Twitter"
                >
                  <i className="ri-twitter-x-line text-base"></i>
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                  }}
                  className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 border border-background-200/60 text-foreground-400 hover:text-secondary-400 hover:border-secondary-400/40 transition-colors duration-200 cursor-pointer"
                  aria-label="复制链接"
                >
                  <i className="ri-link text-base"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-8 pb-8">
            <RelatedArticles articles={relatedArticles} />
          </div>

          {/* Back to Home */}
          <div className="text-center pb-14">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 border border-background-300/60 rounded-full text-sm text-foreground-400 hover:text-foreground-200 hover:border-foreground-300 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
